"use client";

import React, { useEffect, useState } from "react";

// @mui/material components
import Card from "@mui/material/Card";
import {
  CircularProgress,
  Pagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Icons
import { useTranslation } from "react-i18next";
import PaymentApi from "api/payment";
import SoftButton from "components/SoftButton";
import FormField from "layouts/pages/account/components/FormField";
import SoftSelect from "components/SoftSelect";
import ActionCell from "./components/ActionCell";

function Expenses() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editId, setEditId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const userData = JSON.parse(localStorage.getItem("user"));
  const role = userData?.business_role_id || null;
  const dashboard_access = role === "warehouse_owner" || role === "admin";

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // eslint-disable-next-line
  const [successMessage, setSuccessMessage] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    amount: "",
    description: "",
    expense_payment_type: "",
    date: "",
  });

  // Form validation
  const [formErrors, setFormErrors] = useState({
    title: "",
    amount: "",
    description: "",
    expense_payment_type: "",
    date: "",
  });

  const { t } = useTranslation();

  const handleGetOrders = (page) => {
    const token = localStorage.getItem("token");

    if (!token) {
      console.error("No token found in localStorage.");
      setError("No token found in localStorage.");
      setLoading(false);
      return;
    }

    setLoading(true);

    PaymentApi.GetExpenses(token, page)
      .then((response) => {
        if (response.data) {
          setOrders(response.data.results);
          setTotalPages(Math.ceil(response.data.count / 10));
        } else {
          console.warn("No data found:", response.data.msg);
        }
      })
      .catch((error) => {
        if (error.response) {
          console.error("Error response data:", error.response.data.msg);
        } else {
          console.error("There has been an error.");
        }
        setError(error.response?.data?.msg || error.message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    handleGetOrders(page);
  }, [page]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Modal handlers
  const handleOpenModal = (expense = null) => {
    if (expense) {
      // Edit mode
      setFormData({
        title: expense.title,
        amount: expense.amount?.toString(),
        description: expense.description,
        expense_payment_type: expense.expense_payment_type,
        date: expense.date,
      });
      setEditId(expense.id);
    } else {
      // Create mode
      setFormData({
        title: "",
        amount: "",
        description: "",
        expense_payment_type: "",
        date: "",
      });
      setEditId(null);
    }

    setFormErrors({
      title: "",
      amount: "",
      description: "",
      expense_payment_type: "",
      date: "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditId(null);
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...formErrors };

    // Validate title (required)
    if (!formData.title) {
      newErrors.title = t("Le titre est requis");
      valid = false;
    }

    // Validate date (required)
    if (!formData.date) {
      newErrors.date = t("La date est requise");
      valid = false;
    }

    // Validate amount (required and must be a number)
    if (!formData.amount) {
      newErrors.amount = t("Le montant est requis");
      valid = false;
    } else if (
      isNaN(Number.parseFloat(formData.amount)) ||
      Number.parseFloat(formData.amount) <= 0
    ) {
      newErrors.amount = t("Le montant doit être un nombre positif");
      valid = false;
    }

    // Validate description (required)
    if (!formData.description) {
      newErrors.description = t("La description est requise");
      valid = false;
    }

    setFormErrors(newErrors);
    return valid;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    setSubmitting(true);
    const expenseData = {
      title: formData.title,
      amount: Number.parseFloat(formData.amount),
      description: formData.description,
      expense_payment_type: formData.expense_payment_type || null,
      date: formData.date,
    };

    const apiCall = editId
      ? PaymentApi.UpdateExpense(token, editId, expenseData)
      : PaymentApi.CreateExpense(token, expenseData);

    apiCall
      .then(() => {
        setSuccessMessage(
          editId
            ? t("Dépense mise à jour avec succès")
            : t("Dépense créée avec succès")
        );
        handleGetOrders(page);
        handleCloseModal();
      })
      .catch((error) => {
        setError(
          error.response?.data?.msg || t("Erreur lors de l’enregistrement")
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleEditExpense = async (id) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const response = await PaymentApi.GetExpenseById(token, id);
      const expense = response.data;

      setFormData({
        title: expense.title,
        amount: expense.amount?.toString(),
        description: expense.description,
        expense_payment_type: expense.expense_payment_type,
        date: expense.date,
      });

      setEditId(expense.id);
      setFormErrors({
        title: "",
        amount: "",
        description: "",
        expense_payment_type: "",
        date: "",
      });
      setOpenModal(true);
    } catch (error) {
      setError(
        error.response?.data?.msg ||
          t("Erreur lors de la récupération des données")
      );
    }
  };

  const handleDeleteExpense = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteExpense = () => {
    const token = localStorage.getItem("token");
    if (!token || !deleteId) return;
    setLoadingDelete(true);

    PaymentApi.DeleteExpense(token, deleteId)
      .then(() => {
        handleGetOrders(page);
        setOpenDeleteDialog(false);
        setDeleteId(null);
      })
      .catch((error) => {
        setError(
          error.response?.data?.msg || t("Erreur lors de la suppression")
        );
        setOpenDeleteDialog(false);
        setDeleteId(null);
        setLoadingDelete(false);
      });
  };

  const cancelDeleteExpense = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };

  const columns = React.useMemo(() => {
    const baseColumns = [
      { Header: t("Titre"), accessor: "title" },
      { Header: t("Montant"), accessor: "amount" },
      { Header: t("Description"), accessor: "description" },
      { Header: t("Date de création"), accessor: "created_at" },
      { Header: t("Date de mise à jour"), accessor: "updated_at" },
      { Header: t("Type de paiement"), accessor: "expense_payment_type" },
      { Header: t("Paiement"), accessor: "payment" },
      { Header: t("Entrepôt"), accessor: "warehouse" },
    ];

    if (dashboard_access) {
      baseColumns.push({
        Header: t("action"),
        accessor: "action",
        Cell: ({ row }) => (
          <ActionCell
            id={row.original.id}
            onEdit={handleEditExpense}
            onDelete={handleDeleteExpense}
          />
        ),
      });
    }

    return baseColumns;
    // eslint-disable-next-line
  }, [t]);

  const rows = React.useMemo(() => {
    return orders.map((expense) => ({
      id: expense.id,
      title: expense.title,
      amount: `${expense.amount} TND`,
      description: expense.description,
      created_at: new Date(expense.created_at).toLocaleString(),
      updated_at: new Date(expense.updated_at).toLocaleString(),
      expense_payment_type:
        expense.expense_payment_type === "cash"
          ? "Espèce"
          : expense.expense_payment_type === "virement"
          ? "Virement"
          : expense.expense_payment_type === "cheque"
          ? "Chèque"
          : "-",
      payment: expense.payment ? expense.payment : "-",
      warehouse: expense.warehouse,
    }));
  }, [orders]);

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox my={3}>
          <SoftTypography color="error">{error}</SoftTypography>
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        minHeight="85vh"
      >
        <SoftBox my={3}>
          <SoftButton
            variant="gradient"
            color="info"
            sx={{ marginRight: 2, marginBottom: 2 }}
            onClick={handleOpenModal}
          >
            + {t("Nouvelle dépense")}
          </SoftButton>
          <Card
            sx={{
              borderRadius: "20px",
              boxShadow:
                "0 12px 24px -6px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.05), 0 0 1px 0 rgba(0, 0, 0, 0.1)",
            }}
          >
            {loading ? (
              <SoftBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={35}
              >
                <CircularProgress sx={{ color: "#1F51FF" }} />
              </SoftBox>
            ) : (
              <>
                <DataTable
                  table={{ columns, rows }}
                  entriesPerPage={false}
                  canSearch={orders.length > 0}
                  columnMinWidth={70}
                />
                {totalPages > 1 && (
                  <Stack
                    spacing={2}
                    sx={{
                      mt: 2,
                      position: "absolute",
                      bottom: 7,
                      left: 100,
                      transform: "translateX(-50%)",
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="secondary"
                    />
                  </Stack>
                )}
              </>
            )}
          </Card>
        </SoftBox>
        <Footer />
      </SoftBox>

      {/* Create - Edit Expense Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editId ? t("Modifier la dépense") : t("Nouvelle dépense")}
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormField
                label={t("Titre")}
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                error={!!formErrors.title}
                helperText={formErrors.title}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField
                label={t("Montant")}
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange}
                error={!!formErrors.amount}
                helperText={formErrors.amount}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField
                label={t("Description")}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                error={!!formErrors.description}
                helperText={formErrors.description}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField
                label={t("Date")}
                name="date"
                type="date"
                value={formData.date}
                onChange={handleInputChange}
                error={!!formErrors.date}
                helperText={formErrors.date}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <SoftSelect
                label={t("Mode de paiement")}
                placeholder={t("Mode de paiement")}
                value={formData.expense_payment_type}
                onChange={(newValue) =>
                  setFormData({
                    ...formData,
                    expense_payment_type: newValue.value,
                  })
                }
                options={[
                  { value: "cash", label: "Espèce" },
                  { value: "virement", label: "Virement" },
                  { value: "cheque", label: "Chèque" },
                ]}
                errorLabel={!!formErrors.expense_payment_type}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton onClick={handleCloseModal} color="secondary">
            {t("Annuler")}
          </SoftButton>
          <SoftButton
            onClick={handleSubmit}
            variant="gradient"
            color="info"
            disabled={submitting}
          >
            {submitting
              ? t("Enregistrement en cours…")
              : editId
              ? t("Mettre à jour")
              : t("Créer")}
          </SoftButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteDialog}
        onClose={cancelDeleteExpense}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          {t("Confirmation de suppression")}
        </DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2">
            {t("Êtes-vous sûr de vouloir supprimer cette dépense ?")}
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          <SoftButton onClick={cancelDeleteExpense} color="secondary">
            {t("Annuler")}
          </SoftButton>
          <SoftButton
            onClick={confirmDeleteExpense}
            color="error"
            variant="gradient"
            disabled={loadingDelete}
          >
            {loadingDelete ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("Supprimer")
            )}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Expenses;
